import {
    Injectable,
    inject,
    signal
} from "@angular/core";

import {
    TranslateService
} from "@ngx-translate/core";

import {
    $state
} from "app/ajs-upgraded-providers";

import {
    filter
} from "rxjs";

import {
    ConfigurationManagerService,
    EntityBase,
    EntityUpdateInfo,
    PrinterDocumentTypeId,
} from "src/app/core";

import {
    DocumentPrintersManagerDialogStateService,
    OpenDialogsService,
    PrinterErrorFiscalDialogService
} from "src/app/dialogs";

import {
    ActiveSaleService,
    SalePaymentService,
    SalePrintingUtilsService,
    SaleTransactionUtilsService,
    SaleUtilsService,
} from "src/app/features/cashregister";

import {
    GenericToastService
} from "src/app/shared/components/toast/generic-toast.component";

import {
    DevLogger
} from "src/app/shared/dev-logger";

import {
    Sales
} from "tilby-models";

@Injectable({
    providedIn: "root",
})
export class SaleBatchPrintService {
    private readonly $state = inject($state);
    private readonly activeSaleService = inject(ActiveSaleService);
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly documentPrintersManager = inject(DocumentPrintersManagerDialogStateService);
    private readonly openDialogsService = inject(OpenDialogsService);
    private readonly printerErrorFiscal = inject(PrinterErrorFiscalDialogService);
    private readonly salePayment = inject(SalePaymentService);
    private readonly salePrintingUtils = inject(SalePrintingUtilsService);
    private readonly saleTransactionUtils = inject(SaleTransactionUtilsService);
    private readonly saleUtilsService = inject(SaleUtilsService);
    private readonly translateService = inject(TranslateService);
    private readonly snackBar = inject(GenericToastService)

    public init() {
        //Start the auto print routine after 10 seconds
        setTimeout(() => this.autoPrintRoutine(), 10000);

        if(!this.configurationManagerService.getPreference("cashregister.disable_external_sales_notification")) {
            EntityBase.entityUpdates$.pipe(
                filter(data => !!data.externalClient && data.entityName === 'sales' && ['CREATED', 'UPDATED'].includes(data.action))
            ).subscribe((data) => this.onSaleUpdate(data));
        }
    }

    private async onSaleUpdate(data: EntityUpdateInfo) {
        if(!data.entity) {
            return;
        }

        const sale = structuredClone(data.entity) as Sales;

        //Abort if sale is emittable and external sales printing is enabled
        if(this.isExternalSalesPrintEnabled() && this.isSaleEmittable(sale)) {
            return;
        }

        if(!this.$state.current.name.includes('kiosk')) {
            const message = data.action === "CREATED"
                ? 'CASHREGISTER.EXTERNAL_SALES_MANAGER.NEW_SALE_ARRIVED'
                : 'CASHREGISTER.EXTERNAL_SALES_MANAGER.SALE_UPDATED';

            const snackBarRef = this.snackBar.show(message, {}, 'CASHREGISTER.EXTERNAL_SALES_MANAGER.HIDE', 'CASHREGISTER.EXTERNAL_SALES_MANAGER.OPEN', { duration: 0 });

            snackBarRef.onAction().subscribe(() => {
                this.$state.go('app.new.cashregister.sale', { id: sale.id }, { inherit: false });
            });
        }
    }

    private async autoPrintRoutine() {
        const restartTimeout = parseInt(this.configurationManagerService.getPreference('cashregister.batch_sale_print.check_interval') || '10') || 10;

        try {
            await this.processPrintableSales();
        } finally {
            //Retart the auto print routine after the timeout
            setTimeout(() => this.autoPrintRoutine(), restartTimeout * 1000);
        }
    }

    /**
     * The flags of the device, as signals: whoever shows them (the sidenav, the settings page) follows the changes made by the others.
     * The flags are saved in the local storage, so they are valid only for the device in use.
     */
    public readonly externalOrdersPrint = signal(this.isExternalOrdersPrintEnabled());
    public readonly externalSalesPrint = signal(this.isExternalSalesPrintEnabled());

    public isExternalOrdersPrintEnabled() {
        return window.localStorage.getItem('deviceSettings::printExternalOrders') === 'true';
    }

    public isExternalSalesPrintEnabled() {
        return window.localStorage.getItem('deviceSettings::printExternalSales') === 'true';
    }

    public setExternalOrdersPrint(value: boolean) {
        window.localStorage.setItem('deviceSettings::printExternalOrders', value.toString());
        this.externalOrdersPrint.set(value);
    }

    public setExternalSalesPrint(value: boolean) {
        window.localStorage.setItem('deviceSettings::printExternalSales', value.toString());
        this.externalSalesPrint.set(value);
    }

    private logEvent(...args: any[]) {
        DevLogger.log('[ SaleBatchPrint ]', ...args);
    }

    /**
     * Checks if a sale is emittable based on the presence of payments and sale items.
     * The sale is considered emittable if it has any payments or sale items and the paid payments cover the final amount.
     * This method mutates the provided sale object, removing any unpaid payments.
     *
     * @param {Sales} sale - the sale object to be checked
     * @param {boolean} allowRefunds - Wether to allow refunds while checking the sale
     * @return {boolean} true if the sale is emittable, false otherwise
     */
    private isSaleEmittable(sale: Sales, allowRefunds?: boolean) {
        //Remove unpaid payments
        sale.payments = sale.payments?.filter((payment) => payment.paid) || [];
        sale.sale_items = sale.sale_items || [];

        //Skip the sale if there are no payments or sale items
        if ((!sale.payments.length && !allowRefunds) || !sale.sale_items.length) {
            return false;
        }

        //Skip the sale if is a refund/void or there is an amount to pay left
        if ((!allowRefunds && sale.final_amount! < 0) || this.salePayment.getAmountLeftToPay(sale) > 0) {
            return false;
        }

        return true;
    }

    public async printSale(saleToPrint: Sales, printerId: number, documentId: PrinterDocumentTypeId | number, options?: { batchOrderMode?: boolean, skipSaleCheck?: boolean, useEReceipt?: boolean, allowRefunds?: boolean }) {
        if(!options?.skipSaleCheck && !this.isSaleEmittable(saleToPrint, options?.allowRefunds)) {
            return;
        }

        const docType = Object.keys(saleToPrint.e_invoice || {}).length ? 'e_invoice' : documentId;
        const useEReceipt = options?.useEReceipt ?? !!(saleToPrint.sale_customer?.email && !saleToPrint.sale_customer?.disable_mail_receipts);

        this.logEvent(`Printing sale ${saleToPrint.uuid}`);
        let documentData = await this.documentPrintersManager.getPrinterDocumentData(printerId, docType, { eReceipt: useEReceipt });

        const {
            salePayments,
            tipSaleItems,
            priceChanges,
            error
        } = await this.salePayment.processPayments(saleToPrint, documentData);

        saleToPrint.payments = salePayments;

        if(error) {
            throw error;
        }

        let needsRecalculation = false;

        if(tipSaleItems?.length) {
            saleToPrint.sale_items = saleToPrint.sale_items || [];
            saleToPrint.sale_items.push(...tipSaleItems);
            needsRecalculation = true;
        }

        if(priceChanges?.length) {
            saleToPrint.price_changes = saleToPrint.price_changes || [];
            saleToPrint.price_changes.push(...priceChanges);
            needsRecalculation = true;
        }

        if(needsRecalculation) {
            this.saleUtilsService.calculateSalePrices(saleToPrint);
        }

        await this.salePayment.processSaleChange(saleToPrint);

        const result = await this.activeSaleService.emitDocument(saleToPrint, documentData, { batchOrderMode: options?.batchOrderMode });

        if ('printError' in result) {
            throw result.printError;
        }

        return result;
    }

    private async processPrintableSales() {
        const isExternalSalesPrintEnabled = this.isExternalSalesPrintEnabled();
        const isExternalOrdersPrintEnabled = this.isExternalOrdersPrintEnabled();

        //Skip if external sales are not enabled
        if (!isExternalSalesPrintEnabled && !isExternalOrdersPrintEnabled) {
            return;
        }

        const defaultPrinterId = parseInt(this.configurationManagerService.getPreference('cashregister.batch_sale_print.default_printer_id') || '');
        const defaultDocumentPref = this.configurationManagerService.getPreference('cashregister.batch_sale_print.default_document_id') || '';
        const defaultDocumentId = parseInt(defaultDocumentPref) || defaultDocumentPref as PrinterDocumentTypeId | number;

        const sales = await this.saleTransactionUtils.fetchOpenSales({ prepareSales: true });

        for (const saleToPrint of sales) {
            //Skip the sale if it's open in the cashregister
            if (this.activeSaleService.currentSale?.uuid === saleToPrint.uuid) {
                continue;
            }

            if (isExternalSalesPrintEnabled && defaultPrinterId && defaultDocumentId) {
                try {
                    const result = await this.printSale(saleToPrint, defaultPrinterId, defaultDocumentId, { batchOrderMode: isExternalOrdersPrintEnabled });

                    //Sale was printed, skip the order processing as it's already handled by the active sale service
                    if (result) {
                        continue;
                    }
                } catch (error) {
                    this.logEvent(`Error printing sale ${saleToPrint.uuid}`, error);
                    this.openDialogsService.openSnackBarTilby(this.printerErrorFiscal.translateError(error), this.translateService.instant('MISC.OK'));
                    //Skip to the next sale as there was an error and we don't want to print the exits
                    continue;
                }
            }

            if (isExternalOrdersPrintEnabled && saleToPrint.auto_print_order) {
                this.logEvent(`Sending exits for sale ${saleToPrint.uuid}`);
                let results = await this.salePrintingUtils.sendExitToPrinter(saleToPrint, [], { batchMode: true });

                if(results.length) {
                    this.logEvent(`Exits processed for sale ${saleToPrint.uuid}`);
                } else {
                    this.logEvent(`No exits processed for sale ${saleToPrint.uuid}`);
                }
            }
        }
    }
}
